import cx from 'classnames';
import Modal from 'react-modal';

import {Icon, IconName, TestAttribute} from 'components';

import './styles.scss';

export type DialogMaxWidth = 'xss' | 'xs' | 'sm' | 'md' | 'lg' | 'xl' | false;
export type DialogMinWidth = '1/4' | '1/2' | '3/4' | false;

type Props = {
    open: boolean;
    title?: string | React.ReactElement;
    fullScreen?: boolean;
    fullWidth?: boolean;
    maxWidth?: DialogMaxWidth;
    minWidth?: DialogMinWidth;
    className?: string;
    children?: React.ReactNode;
    closeIcon?: boolean;
    closeTimeoutMS?: number;
    titleIcon?: IconName;
    onTitleIconClick?: () => void;
    onClose?: () => void;
}

type SectionProps = {
    children: React.ReactNode;
    className?: string;
}

export const DialogTitle = ({children, className}: SectionProps) => (
    <div className={cx('vs-dialog-title tw-relative tw-px-5 tw-py-4 tw-flex tw-items-center', className)}>
        {children}
    </div>
);

export const DialogContent = ({children, className}: SectionProps) => (
    <div className={cx('vs-dialog-content tw-flex-auto tw-overflow-y-auto tw-px-5 tw-py-6', className)}>
        {children}
    </div>
);

export const DialogActions = ({children, className}: SectionProps) => (
    <div className={cx('vs-dialog-actions tw-flex tw-items-center tw-justify-end tw-p-5', className)}>
        {children}
    </div>
);

export const Dialog = ({
    open, title, fullWidth, fullScreen, maxWidth, minWidth, children, closeIcon, onClose,
    closeTimeoutMS = 200, className, titleIcon, onTitleIconClick
}: Props) => (
    <Modal
        isOpen={open}
        closeTimeoutMS={closeTimeoutMS}
        ariaHideApp={false}
        overlayClassName='vs-dialog-overlay tw-bg-dark tw-bg-opacity-70 tw-flex tw-fixed
            tw-opacity-0 tw-z-modal tw-inset-0 tw-justify-center tw-items-center tw-transition-opacity'
        className={cx(
            'vs-dialog tw-relative tw-flex tw-flex-col tw-rounded-lg tw-shadow-lg tw-bg-layout',
            'tw-opacity-100 tw-bg-opacity-100', {
                'tw-w-full': fullWidth,
                'tw-w-full tw-h-full': fullScreen,
                'tw-max-w-xs': maxWidth === 'xss', // TODO: fix all modals with proper size names
                'tw-max-w-md': maxWidth === 'xs',
                'tw-max-w-xl': maxWidth === 'sm',
                'tw-max-w-3xl': maxWidth === 'md',
                'tw-max-w-5xl': maxWidth === 'lg'
            }, {
                'tw-min-w-[25%]': minWidth === '1/4',
                'tw-min-w-[50%]': minWidth === '1/2',
                'tw-min-w-[75%]': minWidth === '3/4'
            },
            className)}
    >
        {title &&
            <DialogTitle>
                <TestAttribute testAttribute={{id: 'dialog-title'}}>
                    <h2 className={cx('vs-dialog-title-label tw-text-lg tw-font-bold tw-flex-1',
                        {'tw-mr-5': closeIcon || titleIcon})}
                    >
                        {title}
                    </h2>
                </TestAttribute>
            </DialogTitle>}
        {closeIcon &&
            <TestAttribute testAttribute={{id: 'close-dialog-icon'}}>
                <div className='tw-absolute tw-right-4 tw-top-4 tw-w-6 tw-h-6 tw-bg-dark tw-bg-opacity-5 tw-flex-center
                    hover:tw-bg-opacity-10'
                >
                    <Icon name='close' color='dark' button onClick={onClose} />
                </div>
            </TestAttribute>}
        {titleIcon &&
            <div className='tw-absolute tw-right-4 tw-top-4 tw-w-6 tw-h-6 tw-bg-opacity-5 tw-flex-center hover:tw-bg-opacity-10'>
                <Icon name={titleIcon} color='dark' button onClick={onTitleIconClick} />
            </div>}
        {children}
    </Modal>
);